import React, { useEffect, useState } from 'react';
import { InspectionField, InspectionFieldType, InspectionTemplate } from '../../services/inspectionsApi';
import { fetchDevices, fetchDeviceProperties, fetchAlertContacts, DeviceProperty } from '../../services/api';
import { fetchContactGroups, ContactGroup } from '../../services/alertsApi';
import { Device } from '../../types';
import { canDeviate } from './evaluate';
import { ui } from '../../styles/shared';

/** Redigerare för en mall: namn, typ och listan med fält. */

const TYPE_LABELS: Record<InspectionFieldType, string> = {
    heading: 'Rubrik',
    checkbox: 'Kryssruta',
    select: 'Lista med val',
    date: 'Datum',
    number: 'Siffra',
    text: 'Text',
};

/** Förslag som visas när ingen grupp finns ännu. */
export const DEFAULT_GROUPS = ['Brandskydd', 'Underhåll', 'Livsmedel', 'Övrigt'];

// Stilar från den gemensamma stilfilen (styles/shared.ts → ui).
const input = ui.input;
const label = ui.label;
const btn = (bg: string, color = 'white'): React.CSSProperties => ({ ...ui.btnPrimary, backgroundColor: bg, color });
const iconBtn: React.CSSProperties = { background: 'none', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: '4px 8px', fontSize: 13 };

let keyCounter = 0;
const newKey = () => `f_${Date.now().toString(36)}${(keyCounter++).toString(36)}`;

const blankField = (type: InspectionFieldType): InspectionField => {
    const f: InspectionField = { key: newKey(), type, label: '' };
    if (type === 'checkbox') { f.expected = true; f.deviation = true; }
    if (type === 'select') f.options = ['Bra', 'Anmärkning'];
    if (type === 'select') f.deviationValues = ['Anmärkning'];
    return f;
};

interface Props {
    template: InspectionTemplate | null;   // null = ny mall
    groups?: string[];                     // grupper som redan används, visas som förslag
    onSave: (t: Partial<InspectionTemplate>) => Promise<void>;
    onCancel: () => void;
}

const TemplateEditor: React.FC<Props> = ({ template, groups = [], onSave, onCancel }) => {
    const [name, setName] = useState(template?.name ?? '');
    const [category, setCategory] = useState<string>(template?.category ?? '');
    const groupSuggestions = Array.from(new Set([...groups, ...DEFAULT_GROUPS]));
    const [description, setDescription] = useState(template?.description ?? '');
    // Kopia av fälten så att redigeringen inte ändrar mallen i listan innan den sparas.
    const [fields, setFields] = useState<InspectionField[]>(template?.fields?.length ? template.fields.map(f => ({ ...f })) : [blankField('checkbox')]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [devices, setDevices] = useState<Device[]>([]);
    const [props, setProps] = useState<Record<string, DeviceProperty[]>>({});
    const [contacts, setContacts] = useState<{ id: number; name: string; email?: string | null }[]>([]);
    const [contactGroups, setContactGroups] = useState<ContactGroup[]>([]);
    // "Meddela vid klart": grupp eller person, kodat som "g:<id>" / "c:<id>".
    const [notifyDone, setNotifyDone] = useState<string>(
        template?.notify_done_group_id ? `g:${template.notify_done_group_id}` : template?.notify_done_contact_id ? `c:${template.notify_done_contact_id}` : ''
    );

    useEffect(() => {
        fetchDevices().then(d => setDevices(d.filter(x => x.ieee_address))).catch(() => { /* givare är valfritt */ });
        fetchAlertContacts().then(c => setContacts(c)).catch(() => { /* kontakt är valfritt */ });
        fetchContactGroups().then(g => setContactGroups(g)).catch(() => { /* grupp är valfritt */ });
    }, []);

    const loadProps = async (ieee: string) => {
        if (props[ieee]) return;
        const p = await fetchDeviceProperties(ieee);
        // Anropet ger nyckel, namn och enhet (inget värde), så visa alla egenskaper.
        setProps(prev => ({ ...prev, [ieee]: p ?? [] }));
    };
    useEffect(() => {
        fields.forEach(f => { if (f.sensor?.ieee_address) loadProps(f.sensor.ieee_address); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.length]);

    const update = (i: number, patch: Partial<InspectionField>) =>
        setFields(prev => prev.map((f, idx) => idx === i ? { ...f, ...patch } : f));
    const remove = (i: number) => setFields(prev => prev.filter((_, idx) => idx !== i));
    const move = (i: number, dir: -1 | 1) => setFields(prev => {
        const j = i + dir;
        if (j < 0 || j >= prev.length) return prev;
        const copy = [...prev];
        [copy[i], copy[j]] = [copy[j], copy[i]];
        return copy;
    });
    // Dra och släpp: fältet på plats `from` hamnar före det som nu står på plats `to` (to = längden betyder sist).
    const moveTo = (from: number, to: number) => setFields(prev => {
        if (to === from || to === from + 1) return prev;
        const copy = [...prev];
        const [f] = copy.splice(from, 1);
        copy.splice(to > from ? to - 1 : to, 0, f);
        return copy;
    });
    const [dragFrom, setDragFrom] = useState<number | null>(null);
    const [dropAt, setDropAt] = useState<number | null>(null);
    const endDrag = () => { setDragFrom(null); setDropAt(null); };
    // Linjen visas bara där en släppning faktiskt flyttar fältet.
    const showDrop = (at: number) => dragFrom !== null && dropAt === at && at !== dragFrom && at !== dragFrom + 1;
    const add = (type: InspectionFieldType) => setFields(prev => [...prev, blankField(type)]);
    // Kopian får en ny nyckel och läggs direkt under originalet; listor och givare kopieras med.
    const clone = (i: number) => setFields(prev => {
        const src = prev[i];
        const copy: InspectionField = {
            ...src,
            key: newKey(),
            options: src.options ? [...src.options] : undefined,
            deviationValues: src.deviationValues ? [...src.deviationValues] : undefined,
            sensor: src.sensor ? { ...src.sensor } : src.sensor,
        };
        return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)];
    });

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!name.trim()) { setError('Mallen måste ha ett namn'); return; }
        if (fields.some(f => !f.label.trim())) { setError('Alla fält måste ha en rubrik'); return; }
        setSaving(true);
        try {
            await onSave({ name: name.trim(), category: category.trim() || 'Övrigt', description: description.trim() || null, fields, active: template?.id ? template.active : true, notify_done_contact_id: notifyDone.startsWith('c:') ? Number(notifyDone.slice(2)) : null,
                notify_done_group_id: notifyDone.startsWith('g:') ? Number(notifyDone.slice(2)) : null });
        } catch (err: any) {
            setError(err.message || 'Kunde inte spara mallen');
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} style={{ background: 'white', border: '1px solid #ddd', borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <h2 style={{ marginTop: 0 }}>{template?.id ? 'Ändra mall' : template ? 'Ny mall (kopia)' : 'Ny mall'}</h2>
            {error && <div style={{ background: '#f8d7da', color: '#721c24', padding: 10, borderRadius: 4, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 2fr) minmax(140px, 1fr)', gap: 12, marginBottom: 12 }}>
                <div>
                    <label style={label}>Namn</label>
                    <input value={name} onChange={e => setName(e.target.value)} placeholder="T.ex. Brandskydd, månadskontroll" style={input} />
                </div>
                <div>
                    <label style={label}>Grupp</label>
                    <input list="inspection-groups" value={category} onChange={e => setCategory(e.target.value)} placeholder="T.ex. Brandskydd eller Underhåll" style={input} />
                    <datalist id="inspection-groups">
                        {groupSuggestions.map(g => <option key={g} value={g} />)}
                    </datalist>
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 2fr) minmax(200px, 1fr)', gap: 12, marginBottom: 16 }}>
                <div>
                    <label style={label}>Beskrivning (valfritt)</label>
                    <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Kort om vad kontrollen gäller" style={input} />
                </div>
                <div>
                    <label style={label}>Meddela vid klart <a href="/contacts" style={{ fontWeight: 'normal', fontSize: 12, marginLeft: 6 }}>Hantera kontakter</a></label>
                    <select value={notifyDone} onChange={e => setNotifyDone(e.target.value)} style={input}>
                        <option value="">Ingen</option>
                        {contactGroups.length > 0 && (
                            <optgroup label="Grupper (alla får mejlet)">
                                {contactGroups.map(g => <option key={`g:${g.id}`} value={`g:${g.id}`}>{g.name} ({g.members.length} pers.)</option>)}
                            </optgroup>
                        )}
                        <optgroup label="Personer">
                            {contacts.map(c => <option key={`c:${c.id}`} value={`c:${c.id}`}>{c.name}{c.email ? ` (${c.email})` : ''}</option>)}
                        </optgroup>
                    </select>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Får ett mejl varje gång en kontroll med mallen är gjord.</div>
                </div>
            </div>

            <h3 style={{ margin: '8px 0 10px', fontSize: 15 }}>Fält</h3>
            <div
                onDragOver={e => { if (dragFrom !== null) e.preventDefault(); }}
                onDrop={e => { e.preventDefault(); if (dragFrom !== null && dropAt !== null) moveTo(dragFrom, dropAt); endDrag(); }}
            >
            {fields.map((f, i) => (
                <div key={f.key}
                    onDragOver={e => {
                        if (dragFrom === null) return;
                        e.preventDefault();
                        const r = e.currentTarget.getBoundingClientRect();
                        setDropAt(e.clientY > r.top + r.height / 2 ? i + 1 : i);
                    }}
                    style={{
                        border: '1px solid #e0e0e0', borderRadius: 6, padding: 12, marginBottom: 10, background: f.type === 'heading' ? '#f0f4fa' : '#fafafa',
                        opacity: dragFrom === i ? 0.4 : 1,
                        boxShadow: showDrop(i) ? '0 -4px 0 #0f3460' : (i === fields.length - 1 && showDrop(fields.length)) ? '0 4px 0 #0f3460' : undefined,
                    }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                        <span
                            draggable
                            onDragStart={e => {
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData('text/plain', f.key); // krävs i Firefox
                                const card = e.currentTarget.parentElement?.parentElement;
                                if (card) e.dataTransfer.setDragImage(card, 20, 20);
                                setDragFrom(i);
                            }}
                            onDragEnd={endDrag}
                            title="Dra för att flytta"
                            style={{ cursor: 'grab', color: '#888', fontSize: 16, lineHeight: 1, padding: '2px 4px', userSelect: 'none' }}
                        >⠿</span>
                        <span style={{ fontSize: 12, background: '#0f3460', color: 'white', borderRadius: 10, padding: '2px 8px' }}>{TYPE_LABELS[f.type]}</span>
                        {f.type !== 'heading' && (
                            <span title={canDeviate(f) ? 'Fältet kan ge en avvikelse' : 'Fältet är bara en notering'} style={{ fontSize: 11, borderRadius: 10, padding: '2px 8px', background: canDeviate(f) ? '#f8d7da' : '#eee', color: canDeviate(f) ? '#721c24' : '#666' }}>
                                {canDeviate(f) ? 'avvikelse' : 'notering'}
                            </span>
                        )}
                        <input value={f.label} onChange={e => update(i, { label: e.target.value })} placeholder={f.type === 'heading' ? 'Rubriktext' : 'Vad ska kontrolleras?'} style={{ ...input, flex: 1, minWidth: 180 }} />
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0} style={iconBtn} title="Flytta upp">↑</button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === fields.length - 1} style={iconBtn} title="Flytta ner">↓</button>
                        <button type="button" onClick={() => clone(i)} style={iconBtn} title="Kopiera fältet">⧉</button>
                        <button type="button" onClick={() => remove(i)} style={{ ...iconBtn, color: '#dc3545' }} title="Ta bort">✕</button>
                    </div>

                    {f.type !== 'heading' && (
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                <input type="checkbox" checked={!!f.required} onChange={e => update(i, { required: e.target.checked })} /> Måste fyllas i
                            </label>

                            {f.type === 'checkbox' && (
                                <>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                        <input type="checkbox" checked={f.deviation !== false} onChange={e => update(i, { deviation: e.target.checked })} />
                                        Ger avvikelse om rutan {f.expected === false ? 'är ikryssad' : 'inte är ikryssad'}
                                    </label>
                                    {f.deviation !== false && (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                            I ordning när rutan är
                                            <select value={f.expected === false ? 'off' : 'on'} onChange={e => update(i, { expected: e.target.value === 'on' })} style={{ ...input, width: 'auto' }}>
                                                <option value="on">ikryssad</option>
                                                <option value="off">tom</option>
                                            </select>
                                        </label>
                                    )}
                                </>
                            )}

                            {f.type === 'select' && (
                                <div style={{ flex: 1, minWidth: 260 }}>
                                    <label style={label}>Val (ett per rad). Kryssa de som räknas som avvikelse.</label>
                                    {(f.options ?? []).map((o, oi) => (
                                        <div key={oi} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                                            <input value={o} onChange={e => {
                                                const opts = [...(f.options ?? [])];
                                                const old = opts[oi]; opts[oi] = e.target.value;
                                                const dev = (f.deviationValues ?? []).map(d => d === old ? e.target.value : d);
                                                update(i, { options: opts, deviationValues: dev });
                                            }} style={{ ...input, maxWidth: 240 }} />
                                            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <input type="checkbox" checked={(f.deviationValues ?? []).includes(o)} onChange={e => {
                                                    const dev = new Set(f.deviationValues ?? []);
                                                    e.target.checked ? dev.add(o) : dev.delete(o);
                                                    update(i, { deviationValues: Array.from(dev) });
                                                }} /> avvikelse
                                            </label>
                                            <button type="button" onClick={() => update(i, { options: (f.options ?? []).filter((_, k) => k !== oi), deviationValues: (f.deviationValues ?? []).filter(d => d !== o) })} style={iconBtn}>✕</button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => update(i, { options: [...(f.options ?? []), ''] })} style={{ ...iconBtn, marginTop: 4 }}>+ Lägg till val</button>
                                </div>
                            )}

                            {f.type === 'date' && (
                                <label style={{ fontSize: 13 }}>
                                    Avvikelse om äldre än (dagar)
                                    <input type="number" min={0} value={f.maxAgeDays ?? ''} onChange={e => update(i, { maxAgeDays: e.target.value === '' ? null : Number(e.target.value) })} style={{ ...input, width: 100, marginLeft: 8 }} placeholder="t.ex. 365" />
                                </label>
                            )}

                            {f.type === 'number' && (
                                <>
                                    <label style={{ fontSize: 13 }}>Lägst
                                        <input type="number" step="any" value={f.min ?? ''} onChange={e => update(i, { min: e.target.value === '' ? null : Number(e.target.value) })} style={{ ...input, width: 90, marginLeft: 6 }} />
                                    </label>
                                    <label style={{ fontSize: 13 }}>Högst
                                        <input type="number" step="any" value={f.max ?? ''} onChange={e => update(i, { max: e.target.value === '' ? null : Number(e.target.value) })} style={{ ...input, width: 90, marginLeft: 6 }} />
                                    </label>
                                    <label style={{ fontSize: 13 }}>Enhet
                                        <input value={f.unit ?? ''} onChange={e => update(i, { unit: e.target.value })} style={{ ...input, width: 70, marginLeft: 6 }} placeholder="°C" />
                                    </label>
                                    {devices.length > 0 && (
                                        <label style={{ fontSize: 13 }}>Fyll i från givare
                                            <select value={f.sensor?.ieee_address ?? ''} onChange={e => {
                                                const ieee = e.target.value;
                                                if (!ieee) { update(i, { sensor: null }); return; }
                                                loadProps(ieee);
                                                update(i, { sensor: { ieee_address: ieee, parameter_key: '' } });
                                            }} style={{ ...input, width: 'auto', marginLeft: 6 }}>
                                                <option value="">Ingen</option>
                                                {devices.map(d => <option key={d.ieee_address} value={d.ieee_address}>{d.name}</option>)}
                                            </select>
                                        </label>
                                    )}
                                    {f.sensor?.ieee_address && (
                                        <label style={{ fontSize: 13 }}>Värde
                                            <select value={f.sensor.parameter_key} onChange={e => update(i, { sensor: { ieee_address: f.sensor!.ieee_address, parameter_key: e.target.value } })} style={{ ...input, width: 'auto', marginLeft: 6 }}>
                                                <option value="">Välj…</option>
                                                {(props[f.sensor.ieee_address] ?? []).map(p => <option key={p.key} value={p.key}>{p.label ?? p.key}{p.unit ? ` (${p.unit})` : ''}</option>)}
                                            </select>
                                        </label>
                                    )}
                                </>
                            )}

                            {f.type === 'text' && (
                                <>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                        <input type="checkbox" checked={!!f.multiline} onChange={e => update(i, { multiline: e.target.checked })} /> Flera rader
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }} title="Fältet fylls i med namnet på den som öppnar kontrollen. Går att ändra.">
                                        <input type="checkbox" checked={!!f.contact} onChange={e => update(i, { contact: e.target.checked })} /> Kontakt (fylls i med den som gör kontrollen)
                                    </label>
                                </>
                            )}

                            <label style={{ fontSize: 13, flex: 1, minWidth: 200 }}>Hjälptext
                                <input value={f.help ?? ''} onChange={e => update(i, { help: e.target.value })} style={{ ...input, marginTop: 4 }} placeholder="Visas under fältet (valfritt)" />
                            </label>
                        </div>
                    )}
                </div>
            ))}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0 18px' }}>
                <span style={{ fontSize: 13, color: '#666', alignSelf: 'center' }}>Lägg till:</span>
                {(Object.keys(TYPE_LABELS) as InspectionFieldType[]).map(t => (
                    <button key={t} type="button" onClick={() => add(t)} style={{ ...iconBtn, background: 'white' }}>+ {TYPE_LABELS[t]}</button>
                ))}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving} style={btn('#28a745')}>{saving ? 'Sparar…' : 'Spara mall'}</button>
                <button type="button" onClick={onCancel} style={btn('#6c757d')}>Avbryt</button>
            </div>
        </form>
    );
};

export default TemplateEditor;
